import React, {useEffect, useRef, useState} from 'react';
import {Row, Col} from 'react-bootstrap';
import {useLocation, useNavigate} from 'react-router-dom';
import TaskInfo from './task-info';
import TaskEvents from './task-events/task-events';
import {getTaskDetailsScroller} from '../../../../../utils/scrollUtils';
import {useSelector} from 'react-redux';
import {
  getRiskAssessmentCoreLoadingState,
  getRiskAssessmentCoreTasksState,
  getRiskAssessmentTaskMapping,
  getTaskDetailState,
  selectScrollPosition,
} from '../../../../../redux/selectors/riskAssessmentCore';
import {
  getRiskAssessmentTasks,
  getTaskDetail,
  updateTask,
  updateTaskMedia,
  deleteTaskMedia,
} from '../../../../../redux/risk-assessment-core/riskAssessmentCoreSlice';
import Loader from '../../../../../components/loader/loader';
import {useDispatch} from 'react-redux';
import './task-details.scss';
import Button from '../../../../../components/form/button/button';
import TaskMedia from './task-media/task-media';
import InternalNotes from './internal-notes/internal-notes';
import {getUserRoleName, taskStatusIDS} from '../../../../../utils/constants';
import {allRoutes} from '../../../../../utils/routeConstants';
import {getLoginStateSelector} from '../../../../../redux/selectors/loginSelector';

export default function TaskDetails() {
  const nav = useNavigate();

  const [finalEventData, setFinalEventData] = useState([]);
  const [showTaskMedia, setShowTaskMedia] = useState(false);
  const [showInternalNotes, setShowInternalNotes] = useState(false);
  const [internalNotes, setInternalNotes] = useState([]);

  const location = useLocation();
  const dispatch = useDispatch();
  const currentNavState = location.state;
  const selectedRiskAssessment = currentNavState?.selectedRiskAssessment;
  const task_mapping = useSelector(getRiskAssessmentTaskMapping());
  const isLoading = useSelector(getRiskAssessmentCoreLoadingState());
  const taskList = useSelector(getRiskAssessmentCoreTasksState());
  const taskDetail = useSelector(getTaskDetailState());
  const loginState = useSelector(getLoginStateSelector());
  const {loggedInUser} = loginState;
  const readOnly = getUserRoleName(loggedInUser?.role) === 'general';

  const [loading, setLoading] = useState(true);

  const [taskMedia, setTaskMedia] = useState(
    currentNavState?.selectedTask?.media_details,
  );
  const savedScroll = useSelector(selectScrollPosition());
  const hasRestoredScroll = useRef(false);

  // Restore position after returning from a PUE detail view. The page renders
  // asynchronously and the PUE rows can render at different heights than when we
  // captured (expanded vs collapsed), so a raw pixel offset drifts a few rows.
  // We therefore anchor on the clicked PUE element (data-pue-id) and scroll it to
  // the top of the container, falling back to the pixel offset if the row is not
  // found. We re-apply every animation frame (capped ~4s) so it survives the
  // async render/grow. Restored once per mount and scoped to the task id.
  useEffect(() => {
    const savedPosition = savedScroll?.position;
    const savedTaskId = savedScroll?.taskId;
    const savedEventMappingId = savedScroll?.eventMappingId;
    const currentTaskId = currentNavState?.selectedTask?.id;
    if (
      hasRestoredScroll.current ||
      !savedPosition ||
      !(savedTaskId == null || savedTaskId === currentTaskId)
    ) {
      return;
    }
    hasRestoredScroll.current = true;

    let rafId;
    let cancelled = false;
    let rowFoundAt = null;
    const start = Date.now();
    const apply = () => {
      if (cancelled) return;
      const el = getTaskDetailsScroller();
      if (el) {
        // Prefer anchoring on the exact PUE row the user opened.
        const row =
          savedEventMappingId != null
            ? el.querySelector(`[data-pue-id="${savedEventMappingId}"]`)
            : null;
        if (row) {
          if (rowFoundAt == null) rowFoundAt = Date.now();
          const delta =
            row.getBoundingClientRect().top - el.getBoundingClientRect().top;
          // Only correct on real drift so it is a no-op when stable and doesn't
          // fight the user. Keep re-anchoring briefly after the row appears so
          // rows above settling (notes, thumbnails) can't leave it drifted.
          if (Math.abs(delta) > 1) el.scrollTop += delta;
          if (Date.now() - rowFoundAt > 600) return; // settled → done
        } else {
          // Fallback: pixel offset (clamped) until the row renders.
          const maxScroll = el.scrollHeight - el.clientHeight;
          el.scrollTop = Math.min(savedPosition, maxScroll);
        }
      }
      // Overall cap covers slow task loads where the row appears late.
      if (Date.now() - start < 4000) {
        rafId = requestAnimationFrame(apply);
      }
    };
    rafId = requestAnimationFrame(apply);
    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [savedScroll, currentNavState?.selectedTask?.id]);

 
  


  useEffect(() => {
    if (currentNavState?.selectedTask?.id) {
      fetchTaskDetail(currentNavState.selectedTask.id);
    }
  }, [currentNavState?.selectedTask?.id]);

  useEffect(() => {
    if (taskMedia?.length === 0) {
      setShowTaskMedia(false);
    }
  }, [taskMedia]);

  useEffect(() => {
    if (taskDetail?.id) {
      const notesData = (taskDetail?.internal_note ?? []).filter(
        item => item?.internal_note && String(item.internal_note).length > 0,
      );
      if (notesData?.length > 0) {
        setInternalNotes(notesData);
      }
      setTaskMedia(taskDetail?.media_details);

      if (task_mapping?.size > 0) {
        let final = [];
        for (let [key, value] of task_mapping) {
          if (value.length === 1) {
            final.push(value[0]);
          } else {
            let parent = {...value.find(el => !el.event_sub_category)};
            let childs = [...value.filter(el => el.event_sub_category)];
            parent.exp_unwntd_events =
              childs[0]?.exp_unwntd_events || parent.exp_unwntd_events;
            parent['sub_cat_data'] = childs;
            final.push(parent);
          }
        }
        setFinalEventData([...final]);
      }
      else{
        setFinalEventData([]);

      }
    }
  }, [taskDetail, task_mapping]);

  const fetchTaskDetail = id => {

    setLoading(true);
    dispatch(
      getTaskDetail({
        id,
        callback: resp => {
          // console.log(resp)
          setLoading(false);
        },
      }),
    );
    
  };

  const handleTaskCreation = (id, payload) => {
    dispatch(
      updateTask({
        id,
        payload,
        callback: resp => {
          //   nav(allRoutes.dashboardRoutes.assessmentRoutes.taskDetails, {
          //     state: {
          //         ...location.state,
          //         selectedTask: resp
          //     }
          // })
        },
      }),
    );
  };

  const handleMediaAdd = e => {
    const files = Array.from(e.target.files);
    // console.log('files', files)
    const payload = new FormData();
    files.forEach(file => {
      payload.append('media', file);
    });
    const id = currentNavState.selectedTask.id;
    dispatch(
      updateTaskMedia({
        id,
        payload,
        callback: resp => {
          setTaskMedia([...taskMedia, ...resp]);
        },
      }),
    );
  };

  const handleMediaDelete = media => {
    const media_id=media?.id
    const id = currentNavState.selectedTask.id;
    const payload = {media_id};
    dispatch(
      deleteTaskMedia({
        id,
        payload,
        callback: resp => {
          fetchTaskDetail(id);
        },
      }),
    );
  };

  if(loading) {
    return <Loader />;
  }

  return (
    <div className="task-details-wrapper">
      <header>
        <Row>
          <Col xs={7}>
            <div className="assessment-details">
              <div>
                <img
                  className="product-image"
                  src={selectedRiskAssessment?.product_detail?.image}
                />
              </div>
              <div style={{marginLeft: '0.75rem'}}>
                <div>{selectedRiskAssessment?.name}</div>
                <div>
                  Product line: {selectedRiskAssessment?.product_detail?.name}
                </div>
                <div>
                  {`Progress: ${
                    taskList?.filter(
                      item => item.task_status === taskStatusIDS.completed,
                    )?.length ?? 0
                  }/${taskList?.length ?? 0} task assessed`}
                  {/* {`Progress: ${selectedRiskAssessment?.task?.completed_task}/${selectedRiskAssessment?.task?.total_task} assessed`} */}
                </div>
              </div>
            </div>
          </Col>
          <Col xs={5}>
            <div className="task-right-actions">
              <a onClick={() => setShowInternalNotes(internalNotes?.length > 0)}>
                {`View Internal Notes(${internalNotes?.length})`}
              </a>
              {taskMedia?.length > 0 && (
                <a onClick={() => setShowTaskMedia(true)}>View Media</a>
              )}
             { !readOnly  &&  <div className="upload-media">
                  <Button variant={'secondary-button'}>Upload Media</Button>
                  <input multiple onChange={handleMediaAdd} type="file" />
                </div>}
              {/* {!readOnly ? (
                <div className="upload-media">
                  <Button variant={'secondary-button'}>Upload Media</Button>
                  <input multiple onChange={handleMediaAdd} type="file" />
                </div>
              ) : (
                <></>
              )} */}
            </div>
          </Col>
        </Row>
        <hr />
        <TaskInfo
          taskData={taskDetail}
          handleTaskCreation={handleTaskCreation}
          assessmentType={location.state?.type}
        />
      </header>
      <article>
        <TaskEvents taskData={taskDetail} eventData={finalEventData} />
      </article>
      {showTaskMedia && (
        <TaskMedia
          medias={taskMedia}
          show={showTaskMedia}
          setShow={() => setShowTaskMedia(false)}
          handleDelete={handleMediaDelete}
        />
      )}
      {showInternalNotes && (
        <InternalNotes
          show={showInternalNotes}
          setShow={() => setShowInternalNotes(false)}
          label="Internal Notes"
          notes={internalNotes}
        />
      )}
      {isLoading && <Loader />}
    </div>
  );
}
